import { isNullish } from '@/utils/helper'
import { isCountryOfInstallHongKong } from './countryHelper'

// SOP deployment date - centralized constant
export const SOP_DEPLOYMENT_DATE = new Date('2025-12-15')

// Error messages - centralized for consistency
const ERROR_MESSAGES = {
  INCOMPLETE_QUESTIONNAIRE: 'Complete SOP Questionnaire in Commercial Checklist before save/submitting the offer',
  REQUIRED_FOR_HK: 'SOP Questionnaire is required for Hong Kong installations',
}

/**
 * Checks if a value is unanswered (null, undefined, or empty string)
 */
const isUnanswered = (value) => {
  return isNullish(value) || value === ''
}

/**
 * Validates if SOP questionnaire is complete based on the path taken by the user
 * Returns true if the questionnaire is complete (user has answered all required questions in their chosen path)
 *
 * Flow:
 * - Q0 (isCountryOfInstallHK): Must be answered
 *   - If No → Complete (SOP not required)
 *   - If Yes → Continue to Q1
 *
 * - Q1 (isMainContractSignedAfterAug2025): Required if Q0=Yes
 *   - If No → Complete (SOP not required)
 *   - If Yes → Continue to Q2
 *
 * - Q2 (isOver5Million): Required if Q1=Yes
 *   - If No → Complete (SOP not required)
 *   - If Yes → Continue to Q3
 *
 * - Q3 (isPublicContract): Required if Q2=Yes
 *   - If Yes → Complete (SOP required)
 *   - If No → Continue to Q4
 *
 * - Q4 (isNewBuildingProject): Required if Q2=Yes && Q3=No
 *   - If Yes → Complete (SOP required)
 *   - If No → Continue to Q5
 *
 * - Q5 (isValueOfWorkRequiredBAApproval): Required if Q2=Yes && Q3=No && Q4=No
 *   - If No → Complete (SOP not required)
 *   - If Yes → Continue to Q6
 *
 * - Q6 (isNonResidentialOver5Million): Required if Q2=Yes && Q3=No && Q4=No && Q5=Yes
 *   - Must be answered → Complete (SOP required or not based on answer)
 */
export const validateSOPQuestionnaireAnswer = (answers = {}) => {
  // Q0: Must always be answered
  if (isUnanswered(answers.isCountryOfInstallHK)) {
    throw new Error(ERROR_MESSAGES.INCOMPLETE_QUESTIONNAIRE)
  }

  // If Q0 = No, SOP is not required
  if (!answers.isCountryOfInstallHK) {
    return
  }

  // Q1: Required if Q0 = Yes
  if (isUnanswered(answers.isMainContractSignedAfterAug2025)) {
    throw new Error(ERROR_MESSAGES.INCOMPLETE_QUESTIONNAIRE)
  }

  // If Q1 = No, SOP is not required
  if (!answers.isMainContractSignedAfterAug2025) {
    return
  }

  // Q2: Required if Q1 = Yes
  if (isUnanswered(answers.isOver5Million)) {
    throw new Error(ERROR_MESSAGES.INCOMPLETE_QUESTIONNAIRE)
  }

  if (!answers.isOver5Million) {
    return
  }

  // Q3: Required if Q2 = Yes
  if (isUnanswered(answers.isPublicContract)) {
    throw new Error(ERROR_MESSAGES.INCOMPLETE_QUESTIONNAIRE)
  }

  if (answers.isPublicContract) {
    return
  }

  // Q4: Required if Q3 = No
  if (isUnanswered(answers.isNewBuildingProject)) {
    throw new Error(ERROR_MESSAGES.INCOMPLETE_QUESTIONNAIRE)
  }

  if (answers.isNewBuildingProject) {
    return
  }

  // Q5: Required if Q4 = No
  if (isUnanswered(answers.isValueOfWorkRequiredBAApproval)) {
    throw new Error(ERROR_MESSAGES.INCOMPLETE_QUESTIONNAIRE)
  }

  if (!answers.isValueOfWorkRequiredBAApproval) {
    return
  }

  // Q6: Required if Q5 = Yes
  if (isUnanswered(answers.isNonResidentialOver5Million)) {
    throw new Error(ERROR_MESSAGES.INCOMPLETE_QUESTIONNAIRE)
  }

  // All required questions answered
  return
}

/**
 * Validates SOP questionnaire submission requirements
 * @param {Object} params - Validation parameters
 * @param {string} params.countryOfInstallation - Country of installation
 * @param {boolean} params.isEQuotation - Whether this is an eQuotation
 * @param {Date} params.offerCreateDate - Offer creation date
 * @param {Object} params.SOPQuestionAnswers - SOP questionnaire answers
 * @throws {Error} If SOP validation fails
 */
export const validateSOPSubmission = ({
  countryOfInstallation = '',
  isEQuotation = false,
  offerCreateDate,
  SOPQuestionAnswers = {},
}) => {
  // Only validate for Hong Kong installations
  if (!isCountryOfInstallHongKong(countryOfInstallation, SOPQuestionAnswers)) {
    return
  }

  // eQuotations are exempt
  if (isEQuotation) {
    return
  }

  // Only validate offers created after deployment date
  if (offerCreateDate && offerCreateDate < SOP_DEPLOYMENT_DATE) {
    return
  }

  // Check if questionnaire has been started
  const hasStartedQuestionnaire =
    Object.keys(SOPQuestionAnswers).length > 0 &&
    Object.values(SOPQuestionAnswers).some((value) => !isUnanswered(value))

  if (!hasStartedQuestionnaire) {
    throw new Error(ERROR_MESSAGES.REQUIRED_FOR_HK)
  }

  // Validate questionnaire completeness
  validateSOPQuestionnaireAnswer(SOPQuestionAnswers)
}
